/* eslint-disable */
// @ts-nocheck
import * as R from 'ramda';
import { DIMENSIONS_REQUIRED_FOR_COLUMNS, DEFAULT_ACTIVE_HEADER } from './fraudConstants';
import {
  CUSTOM_EVENT_PREFIX,
  BLOCKED_CUSTOM_EVENT_PREFIX,
  populateRowsFromGroupBy,
  generateTouchToEventTransformers,
  getRowValuesFromColumnHelpers,
} from '../../../common/support/utils';
import type { GroupByResult, GroupByResponse, Query } from '../../types/druidTypes';
import type {
  CodeToNameMapping,
  CodeToDescriptionMapping,
  FraudFilter,
  FraudTablePageState,
  FraudAgentsIndexed,
  ThresholdRules,
  AllFraudRuleTypes,
} from '../../types/fraudTypes';
import type { Rows, Column } from '../../types/tableTypes';
const UNPOPULATED_VALUE = 'Unpopulated';
export const getInitActiveHeader = (pageState: FraudTablePageState | null | undefined): string => {
  return (pageState && pageState.activeHeader) || DEFAULT_ACTIVE_HEADER;
};
// determines if extendWithFraudNames needs to be called
export const codeToNameConversionNecessary = (
  data: GroupByResponse,
  pageState: FraudTablePageState | null | undefined
): boolean => {
  const queryDimensions = R.pathOr([], ['query', 'dimensions'], pageState);
  return queryDimensions.includes('fraud_names') && !!data;
};
// [EG-2380] Convert fraud names to title case with spaces, leaving 'IP' as is
// i.e. DEVICE_CONFLICT => Device Conflict, CONVERSION_TIME => Conversion Time, IP => IP
export const formatFraudName = (name: string) => {
  // special case for IP
  if (name === 'IP') {
    return 'IP';
  }

  // this function will be called even on formatted names, so if a name is already properly
  // formatted (no longer includes underscores), leave the name as is
  if (name.includes(' ')) {
    return name;
  }

  // all other cases
  const lowerCaseWords = name.split('_').map((word) => word.toLowerCase());
  const titleCaseWords = lowerCaseWords.map((word) => {
    if (!word) {
      return '';
    }

    return word[0].toUpperCase() + word.slice(1);
  });
  return titleCaseWords.join(' ');
};
// replace uppercase fraud name with properly formatted fraud name
// calls formatFraudName (above) on each occurrence of a fraud name and replaces it
export const formatFraudNames = (response: GroupByResponse) => {
  const formatHelper = (results: Array<GroupByResult>) => {
    return results.map((v) => {
      if (v.event && v.event.fraud_names) {
        const unformattedFraudName = v.event.fraud_names;
        const path = R.lensPath(['event', 'fraud_names']);
        return R.set(path, formatFraudName(unformattedFraudName), v);
      }

      return v;
    });
  };

  return R.mapObjIndexed(formatHelper, response);
};
// get the corresponding fraud name for a fraud code
export const getFraudNameFromFraudCode = (fraudCode: string, codeToNameMapping: CodeToNameMapping): AllFraudRuleTypes => {
  // EG-2457 Special cases for PERSONA_FRAUD and ONCE_EVER_CAPPED fraud codes
  if (fraudCode === 'PERSONA_FRAUD') {
    return 'PERSONA_FRAUD';
  }

  if (fraudCode === 'ONCE_EVER_CAPPED') {
    return 'ONCE_EVER_CAPPED';
  }

  return codeToNameMapping[fraudCode];
};
export const appendFraudNameWithFraudCode = (fraudName: AllFraudRuleTypes, fraudCode: string): string => {
  // EG-2381, EG-2642 Force Custom and Threshold fraud rules to appear separately in the table by postfixing each with its unique fraud code
  if (fraudName === 'CUSTOM' || fraudName === 'THRESHOLD') {
    return fraudName + fraudCode;
  }

  return fraudName;
};
// extend druid response JSON values that have fraud code values with
// corresponding fraud name values to be rendered in the fraud table
export const extendWithFraudNames = (response: GroupByResponse, codeToNameMapping: CodeToNameMapping) => {
  const extendHelper = (results: Array<GroupByResult>) => {
    return results.map((v) => {
      if (v.event && v.event.fraud_codes) {
        const fraudCode = v.event.fraud_codes;
        let fraudName = getFraudNameFromFraudCode(fraudCode, codeToNameMapping);
        fraudName = appendFraudNameWithFraudCode(fraudName, fraudCode);
        const path = R.lensPath(['event', 'fraud_names']);
        return R.set(path, fraudName, v);
      }

      return v;
    });
  };

  return R.mapObjIndexed(extendHelper, response);
};
// filter table data based on the filter provided in the Druid Query
// this front-end filtering is necessary because a single event may be tagged with multiple fraud names and codes
// for exmaple, a Druid response may include records with fraud name = GEO_CONFLICT and fraud name = IP even though only GEO_CONFLICT was specified in the filter
export const filterTableRows = (filter: FraudFilter, tableRows: Rows, compareByDimension: string): Rows => {
  if (!filter.fields) {
    return tableRows;
  }

  const filterField = filter.fields.filter((filterRow) => filterRow.dimension === compareByDimension);

  // only perform filtering if a filter on the input compareByDimension is present
  if (!filterField.length) {
    return tableRows;
  }

  const values = filterField[0].values;
  return tableRows.filter((row) => {
    // special comparison necessary for fraud_names because of the casing (see formatFraudNames)
    // startsWith is used for fraud_names since Custom values are postfixed with their corresponding fraud codes
    return compareByDimension === 'fraud_names'
      ? values.map((v) => formatFraudName(v)).some((v) => row[compareByDimension] && row[compareByDimension].startsWith(v))
      : values.includes(row[compareByDimension]);
  });
};
export const getFilteredRows = (dimensions: Array<string>, filter: FraudFilter, tableRows: Rows) => {
  if (dimensions.includes('fraud_names') && dimensions.includes('fraud_codes')) {
    // filter on both fraud name and fraud code
    const filteredByName = filterTableRows(filter, tableRows, 'fraud_names');
    return filterTableRows(filter, filteredByName, 'fraud_codes');
  } else if (dimensions.includes('fraud_names')) {
    return filterTableRows(filter, tableRows, 'fraud_names');
  } else if (dimensions.includes('fraud_codes')) {
    return filterTableRows(filter, tableRows, 'fraud_codes');
  } else {
    return tableRows;
  }
};
export const updateDimensionsBasedOnColumns = (columns: Array<any>) => {
  return columns.reduce((acc, column) => {
    const columnToDimensionInfo = DIMENSIONS_REQUIRED_FOR_COLUMNS[column.key];

    if (columnToDimensionInfo) {
      if (acc[columnToDimensionInfo.source] && acc[columnToDimensionInfo.source].dimensions) {
        acc[columnToDimensionInfo.source].dimensions = acc[columnToDimensionInfo.source].dimensions.concat(
          columnToDimensionInfo.dimension
        );
      } else {
        acc[columnToDimensionInfo.source] = {
          dimensions: [columnToDimensionInfo.dimension],
        };
      }
    }

    return acc;
  }, {});
};
// [EG-2656] convert custom event names from eo_custom_event- prefix to eo_custom_event_blocked- prefix
// since these event names are coming from the eo_custom_event_blocked datasource
export const getBlockedCustomEventList = (customEventList: string[]): string[] => {
  return customEventList.map((name) => BLOCKED_CUSTOM_EVENT_PREFIX + name.slice(CUSTOM_EVENT_PREFIX.length));
};
// [EG-3048] remove rows that have a null or undefined value for fraud_names or fraud_codes
export const removeNullishFraudRows = (tableRows: Rows, dimensions: string[]): Rows => {
  const includedFraudDimensions = dimensions.filter((dim) => dim === 'fraud_names' || dim === 'fraud_codes');
  return tableRows.filter((row) => {
    return includedFraudDimensions.every((dim) => {
      return row[dim] !== undefined && row[dim] !== null && row[dim] !== UNPOPULATED_VALUE;
    });
  });
};
export const formatData = (selectedColumns: Column[], metric: string, dimensions: string[], data: GroupByResponse) => {
  const formattedData = formatFraudNames(data);
  const eoInstallBuckets = {
    seconds_from_last_attributed_touch_to_event: [
      [0, 9],
      [10, 29],
      [30, 60],
    ],
    days_from_last_attributed_touch_to_event: [
      [0, 0],
      [1, 90],
    ],
  };
  const eoInstallBucketsToUse = selectedColumns
    .filter((column) => {
      return Object.keys(DIMENSIONS_REQUIRED_FOR_COLUMNS).includes(column.group && column.group.key);
    })
    .reduce((acc, column) => {
      const dimension = DIMENSIONS_REQUIRED_FOR_COLUMNS[column.group && column.group.key].dimension;
      acc[dimension] = eoInstallBuckets[dimension];
      return acc;
    }, {});
  const populatedData = populateRowsFromGroupBy(dimensions, formattedData, metric, {
    ...generateTouchToEventTransformers(['eo_install'], {
      eo_install: eoInstallBucketsToUse,
    }),
  });
  return getRowValuesFromColumnHelpers(selectedColumns, populatedData);
};
// [EG-2380] Use fraud_codes field in place of fraud_names to avoid array isues with druid
export const maskFraudNamesWithFraudCodes = (query: Query) => {
  if (query.dimensions && query.dimensions.includes('fraud_names')) {
    const fraudNamesIndex = query.dimensions.indexOf('fraud_names');

    // if fraud_codes is not already a selected dimension, replace fraud_names with fraud_codes
    if (query.dimensions.includes('fraud_codes')) {
      query.dimensions.splice(fraudNamesIndex, 1);
    } else {
      query.dimensions[fraudNamesIndex] = 'fraud_codes';
    }
  }

  return query;
};
// determines if a fraud_names or fraud_codes filter has been selected
export const fraudFilterSelected = (query: Query): boolean => {
  if (!R.path(['filter', 'fields'], query)) {
    return false;
  }

  let fraudFilterFound = false;
  query &&
    query.filter.fields.forEach((field) => {
      if (field.dimension === 'fraud_names' || field.dimension === 'fraud_codes') {
        fraudFilterFound = true;
      }
    });
  return fraudFilterFound;
};
// removes fraud_names and fraud_codes filters
export const stripFraudFilters = (query: Query): Query => {
  if (!R.path(['filter', 'fields'], query)) {
    return query;
  }

  const newQuery = R.clone(query);
  const queryFilter = query.filter;
  const strippedFilter = queryFilter.fields.filter(
    (field) => field.dimension !== 'fraud_names' && field.dimension !== 'fraud_codes'
  );

  if (strippedFilter.length) {
    newQuery.filter.fields = strippedFilter;
  } else {
    newQuery.filter = {};
  }

  return newQuery;
};
// replace fraud_names with fraud_codes to avoid array issues with druid
// remove fraud_names and fraud_codes filters since unblocked datasources will never return data with these filters
// filters on these dimensions will be completed at the UI-level after Cenarius responds instead of at the outgoing query level
export const modifyQueryForFraud = (query: Query): Query => {
  let newQuery = R.clone(query);

  // [EG-2380] Use fraud_codes field in place of fraud_names to avoid array isues with druid
  if (query.dimensions && query.dimensions.includes('fraud_names')) {
    newQuery = maskFraudNamesWithFraudCodes(newQuery);
  }

  // [EG-3109] Remove fraud_names and fraud_codes filters since unblocked datasources will never return data with these filters
  if (fraudFilterSelected(newQuery)) {
    newQuery = stripFraudFilters(newQuery);
  }

  return newQuery;
};
// determine if a dimension is a fraud dimension and included in a user-selected filter
export const isFraudDimensionInFilter = (dimension: string, query?: Query): boolean => {
  if (dimension !== 'fraud_names' && dimension !== 'fraud_codes') {
    return false;
  }

  if (!R.path(['filter', 'fields'], query)) {
    return false;
  }

  let dimensionInFilter = false;
  query &&
    query.filter.fields.forEach((field) => {
      if (field.dimension === dimension) {
        dimensionInFilter = true;
      }
    });
  return dimensionInFilter;
};
export const createThresholdMapping = (
  thresholdRules: ThresholdRules,
  fraudAgents: FraudAgentsIndexed
): CodeToDescriptionMapping => {
  const thresholdMapping = {};
  // lookup and store agent_description and agent_long_description for each rule that has an agent_id
  thresholdRules.forEach((rule) => {
    const { id, agent_id } = rule;

    if (agent_id && fraudAgents[agent_id]) {
      const { agent_description, agent_long_description } = fraudAgents[agent_id];
      const description = agent_description || '[agent description]'; // fallback case should never happen - only included for flow reasons

      const longDescription = agent_long_description || '[agent long description]'; // fallback case should never happen - only included for flow reasons

      thresholdMapping[id] = `${description}: ${longDescription}`;
    }
  });
  return thresholdMapping;
};
